/* eslint-disable class-methods-use-this */
import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.rookooConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'record.created': this.onRecordCreated,
      'record.updated': this.onRecordUpdated,
      'record.deleted': this.onRecordDeleted,
      'copilot.message.created': this.onCopilotMessageCreated,
      'agent.added': this.onAgentAdded,
      'agent.updated': this.onAgentUpdated,
      'planning_event.created': this.onPlanningEventCreated,
      'planning_event.updated': this.onPlanningEventUpdated,
      'planning_event.deleted': this.onPlanningEventDeleted,
      'planning_event.viewing_on': this.onPlanningEventViewingOn,
      'planning_event.viewing_off': this.onPlanningEventViewingOff,
      'planning_event.read': this.onPlanningEventRead,
      'planning_event.unread': this.onPlanningEventUnread,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onRecordCreated = data => {
    const selectedKnowledgebase =
      this.app.$store.getters['knowledgebase/getSelectedLibrary'];

    if (
      !selectedKnowledgebase ||
      selectedKnowledgebase.id !== data.knowledgebase_id
    ) {
      return;
    }

    this.app.$store.dispatch('records/addRecord', data);
  };

  onRecordUpdated = data => {
    const selectedKnowledgebase =
      this.app.$store.getters['knowledgebase/getSelectedLibrary'];

    if (
      !selectedKnowledgebase ||
      selectedKnowledgebase.id !== data.knowledgebase_id
    ) {
      return;
    }

    this.app.$store.dispatch('records/updateRecord', data);

    if (
      data.status === 'PENDING' ||
      data.status === 'SUCCESS' ||
      data.status === 'FAILED'
    ) {
      const currentDocumentsMeta = this.app.$store.getters['documents/getMeta'];
      const focusedRecord = this.app.$store.getters['records/getFocusedRecord'];

      this.app.$store.dispatch('documents/get', {
        knowledgebaseId: selectedKnowledgebase.id,
        recordId: focusedRecord ? focusedRecord.id : null,
        currentPage: currentDocumentsMeta.current_page || 1,
        limit: currentDocumentsMeta.limit || 20,
      });
    }
  };

  onRecordDeleted = data => {
    this.app.$store.dispatch('records/deleteRecord', data);
    this.app.$store.dispatch('documents/deleteDocumentsByRecordId', data.id);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onAgentAdded = () => {
    this.app.$store.dispatch('assistants/get');
  };

  onAgentUpdated = data => {
    emitter.emit(BUS_EVENTS.ASSISTANT_UPDATED, {
      id: data.id,
      agent_id: data.agent_id,
      assistant: data.agent,
    });
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
    if (keys.space) {
      this.app.$store.dispatch('spaces/revalidate', { newKey: keys.space });
      this.app.$store.dispatch('planningResources/get');
    }
  };

  onPlanningEventCreated = data => {
    this.app.$store.dispatch('planningEvents/addFromSocket', data);
  };

  onPlanningEventUpdated = data => {
    this.app.$store.dispatch('planningEvents/updateFromSocket', data);
    emitter.emit(BUS_EVENTS.PLANNING_EVENT_UPDATED, data);
    if (data.allocations?.length) {
      this.app.$store.dispatch('planningAllocations/syncFillColorsFromEvent', {
        allocations: data.allocations,
      });
    }
  };

  onPlanningEventDeleted = data => {
    this.app.$store.dispatch('planningEvents/deleteFromSocket', data);
  };

  onPlanningEventViewingOn = ({ event_id: eventId, user }) => {
    this.app.$store.dispatch('planningEventViewingStatus/add', {
      eventId,
      user,
    });
  };

  onPlanningEventViewingOff = ({ event_id: eventId, user }) => {
    this.app.$store.dispatch('planningEventViewingStatus/remove', {
      eventId,
      user,
    });
  };

  onPlanningEventRead = ({ id }) => {
    this.app.$store.dispatch('planningEvents/setUnreadFromSocket', {
      id,
      unread: false,
    });
  };

  onPlanningEventUnread = ({ id }) => {
    this.app.$store.dispatch('planningEvents/setUnreadFromSocket', {
      id,
      unread: true,
    });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
